import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from '../shared/components/Icon';

export interface DockMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon?: IconName;
  readonly hint?: string;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  /** Draws a hairline above the item, to group commands that do different kinds of thing. */
  readonly separated?: boolean;
  readonly run: () => void;
}

interface DockMenuProps {
  readonly x: number;
  readonly y: number;
  readonly label: string;
  readonly items: readonly DockMenuItem[];
  readonly onClose: () => void;
}

/**
 * The command list behind a right-click on a tab and behind a pane's overflow button.
 *
 * It is portalled to the document rather than drawn inside the pane, so it is never
 * clipped by the pane it belongs to, and it flips itself back inside the viewport when
 * opened near an edge.
 */
export function DockMenu({ x, y, label, items, onClose }: DockMenuProps) {
  const panel = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const element = panel.current;
    if (element === null) return;
    const rect = element.getBoundingClientRect();
    const left = Math.max(8, Math.min(x, window.innerWidth - rect.width - 8));
    const top = Math.max(8, Math.min(y, window.innerHeight - rect.height - 8));
    setPosition({ left, top });
    element.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus();
  }, [x, y]);

  useEffect(() => {
    const dismiss = (event: Event) => {
      if (event.target instanceof Node && panel.current?.contains(event.target) === true) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const buttons = [...(panel.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [])];
      const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = buttons[(current + step + buttons.length) % Math.max(buttons.length, 1)];
      next?.focus();
    };
    window.addEventListener('pointerdown', dismiss, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('pointerdown', dismiss, true);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return createPortal(
    <div className="dock-menu" role="menu" aria-label={label} ref={panel} style={{ left: `${position.left}px`, top: `${position.top}px` }}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          disabled={item.disabled === true}
          className={`dock-menu__item${item.danger === true ? ' dock-menu__item--danger' : ''}${item.separated === true ? ' dock-menu__item--separated' : ''}`}
          onClick={() => {
            onClose();
            item.run();
          }}
        >
          {item.icon === undefined ? <span className="dock-menu__spacer" aria-hidden="true" /> : <Icon name={item.icon} className="icon icon--small" />}
          <span>{item.label}</span>
          {item.hint === undefined ? null : <kbd>{item.hint}</kbd>}
        </button>
      ))}
    </div>,
    document.body,
  );
}
